import '@fs/react-scripts/polyfills'
import { appPath, mergeSentryConfig, sentryDSN } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import { FrontierBaseProviders } from '@fs/zion-root'
import { AuthRoute, Route, Router, Switch } from '@fs/zion-router'
import * as Sentry from '@sentry/browser'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import App from './App'
import './locales'

const AppAdmin = React.lazy(() => import('./AppAdmin'))

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      denyUrls: [
        // Ignore errors from saved pages
        'file:///',
        // From Safari browser extension
        /.*@webkit-masked-url.*/,
      ],
      ignoreErrors: [
        // Happens in WebKit, particularly iOS Mail. Unresolved WebKit issue: https://bugs.webkit.org/show_bug.cgi?id=197050
        /UnknownError.*Indexed Database server.*/,
        /.*database without an in-progress transaction.*/i,
        // This error happens when a browser aborts a request
        'Request aborted',
        // We have some off-page dependencies that seem to fail occasionally
        /(fbq|gtag) is not defined/,
        /Can't find variable: (fbq|gtag)/,
        // Happens when a user session times out
        /.*status.*401.*/,
        // If this plugin supported retrying CSS chunks, it would reduce this error: https://github.com/mattlewis92/webpack-retry-chunk-load-plugin/issues/2
        /.*chunk.*failed.*/,
        // From Google pageads code
        "Unexpected token '<'",
        // From localforage, used by zion-cache. Unclear how to reproduce: https://developer.apple.com/forums/thread/691468
        /.*database deleted.*/i,
        // From Adobe tracking code
        /.*satellite.*/i,
        // Imperva bot prevention
        /.*challenge script failed.*/i,
        // Caused by the patron’s network, not our code
        /.*Network Error.*/i,
      ],
      integrations: [Sentry.captureConsoleIntegration({ levels: ['error'] })],
      sampleRate: 0.5,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  return (
    <Router basename={base}>
      {/* Admin pages uses AdminRoot. All other patron pages use the standard Root */}
      <Switch>
        <AuthRoute path="/admin" component={AppAdmin} />
        <Route component={App} />
      </Switch>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockActivities={mockActivities} mockFlags={mockFlags}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
    {process.env.NODE_ENV === 'development' && <RebuggerClient />}
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))
